import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, Pause, RotateCcw, Volume2, VolumeX, Sparkles, Loader2, Lock } from "lucide-react";
import { NARRATORS, chapterLabel } from "../data/book";
import { useLang } from "../i18n";
import { useAuth } from "../auth";
import api from "../api";
import useHouseLoop from "../hooks/useHouseLoop";

const BARS = Array.from({ length: 26 });

const TeaserPlayer = ({ story, limited = null }) => {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const text = story[lang] ?? story.it;
  const scenes = useMemo(
    () => text.split(/(?<=[.?!…])\s+/).map((s) => s.trim()).filter((s) => s.length > 3).slice(0, 4),
    [text]
  );
  const total = Math.max(15000, text.split(" ").length * 430);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioOn, setAudioOn] = useState(true);
  const [video, setVideo] = useState(null);
  const [capped, setCapped] = useState(false);
  const loop = useHouseLoop();
  const LIMIT = 5000; // anteprima video: 5 secondi per chi non ha accesso
  const narrRef = useRef(null);
  const [hasNarr, setHasNarr] = useState(false);

  useEffect(() => {
    const a = new Audio(`${process.env.REACT_APP_BACKEND_URL}/api/files/${story.id}_${lang}.mp3`);
    a.preload = "auto";
    const ok = () => setHasNarr(true);
    const bad = () => setHasNarr(false);
    a.addEventListener("canplaythrough", ok);
    a.addEventListener("error", bad);
    narrRef.current = a;
    return () => { a.pause(); a.removeEventListener("canplaythrough", ok); a.removeEventListener("error", bad); narrRef.current = null; };
  }, [story.id, lang]);

  const startSound = () => {
    if (!audioOn) return;
    loop.start(story.bpm);
    if (narrRef.current) { narrRef.current.currentTime = 0; narrRef.current.play().catch(() => {}); }
  };
  const stopSound = () => {
    loop.stop();
    if (narrRef.current) narrRef.current.pause();
  };

  useEffect(() => {
    setElapsed(0); setPlaying(false);
    api.get(`/videos/${story.id}`).then(({ data }) => setVideo(data)).catch(() => {});
  }, [story.id, lang]);

  useEffect(() => {
    if (!playing) return;
    const iv = setInterval(() => {
      setElapsed((e) => {
        const cap = limited ? Math.min(LIMIT, total) : total;
        if (e + 100 >= cap) {
          setPlaying(false);
          if (limited) setCapped(true);
          return cap;
        }
        return e + 100;
      });
    }, 100);
    return () => clearInterval(iv);
  }, [playing, total, limited]);

  useEffect(() => {
    if (playing && audioOn && video?.status !== "ready") {
      loop.start(story.bpm);
      if (hasNarr && narrRef.current) { narrRef.current.currentTime = 0; narrRef.current.play().catch(() => {}); }
    } else {
      loop.stop();
      if (narrRef.current) narrRef.current.pause();
    }
    return () => { loop.stop(); if (narrRef.current) narrRef.current.pause(); };
  }, [playing, audioOn, story.bpm, video, hasNarr]); // eslint-disable-line react-hooks/exhaustive-deps

  const idx = Math.min(scenes.length - 1, Math.floor((elapsed / total) * scenes.length));
  const done = elapsed >= total;
  const started = elapsed > 0 && !done;
  const nar = NARRATORS[story.narrator];
  const isAdmin = user && user.role === "admin";
  const fmt = (ms) => {
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };
  const seek = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setElapsed(Math.floor(((e.clientX - r.left) / r.width) * total));
  };

  const generateVideo = async () => {
    try {
      const { data } = await api.post(`/videos/generate/${story.id}`);
      setVideo(data);
      const poll = setInterval(async () => {
        try {
          const { data: v } = await api.get(`/videos/${story.id}`);
          setVideo(v);
          if (v.status === "ready" || v.status === "error") clearInterval(poll);
        } catch { clearInterval(poll); }
      }, 5000);
    } catch { /* ignored */ }
  };

  const hasVideo = video?.status === "ready" && video?.url;

  return (
    <div
      data-testid="teaser-player"
      className="relative rounded-2xl border border-white/10 bg-console overflow-hidden transition-shadow duration-500"
      style={{ boxShadow: playing && !hasVideo ? `0 0 50px ${nar.dim}, 0 0 18px rgba(255,85,0,0.25)` : "none" }}
    >
      <div className="flex items-center justify-between px-4 sm:px-5 h-11 border-b border-white/10 font-mono2 text-[10px] sm:text-xs tracking-[0.25em] uppercase">
        <span className="text-mist">{hasVideo ? "AI clip" : t("player_short")} · {chapterLabel(story, lang)}</span>
        <span className="flex items-center gap-3">
          <button
            data-testid="teaser-audio-toggle"
            onClick={() => setAudioOn(!audioOn)}
            className={`flex items-center gap-1.5 transition-colors ${audioOn ? "text-gold" : "text-mute hover:text-mist"}`}
          >
            {audioOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
            {t("player_audio")}
          </button>
          <span style={{ color: nar.color }}>{nar[lang] ?? nar.it}</span>
          <span className="text-gold">{story.bpm} BPM</span>
        </span>
      </div>

      {hasVideo ? (
        <video
          data-testid="teaser-video"
          src={`${process.env.REACT_APP_BACKEND_URL}${video.url}`}
          poster={`${process.env.REACT_APP_BACKEND_URL}/api/files/${story.id}.jpg`}
          controls
          loop={!limited}
          playsInline
          onPlay={(e) => { if (capped) { e.target.pause(); return; } startSound(); }}
          onTimeUpdate={(e) => { if (limited && e.target.currentTime >= 5) { e.target.pause(); setCapped(true); } }}
          onPause={stopSound}
          onEnded={stopSound}
          className="w-full aspect-video object-cover bg-black"
        />
      ) : (
        <>
          <div className="relative aspect-video flex items-center justify-center px-6 sm:px-12">
            <div className="absolute inset-0 opacity-40"
              style={{ background: `radial-gradient(ellipse 70% 60% at 50% 110%, ${nar.dim}, transparent 70%)` }} />
            <div className="absolute bottom-0 inset-x-0 h-16 flex items-end justify-center gap-[3px] px-6 opacity-50">
              {BARS.map((_, i) => (
                <span key={i} className="eq-bar w-full max-w-[10px] h-full"
                  style={{
                    background: `linear-gradient(to top, ${nar.color}, transparent)`,
                    animationDelay: `${(i % 9) * 0.11}s`,
                    animationDuration: `${0.55 + (i % 5) * 0.14}s`,
                    animationPlayState: playing ? "running" : "paused",
                  }} />
              ))}
            </div>

            {!started && !done && !playing && (
              <div className="relative z-10 text-center">
                <p className="font-serif-it text-lg sm:text-2xl text-mist mb-2">{t("player_note")}</p>
                {isAdmin && video?.status !== "processing" && (
                  <button data-testid="video-ai-generate" onClick={generateVideo}
                    className="mt-3 inline-flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2 font-mono2 text-[10px] tracking-[0.25em] uppercase text-gold hover:bg-gold hover:text-black transition-colors not-italic">
                    <Sparkles className="w-3.5 h-3.5" /> {t("video_ai_btn")}
                  </button>
                )}
                {video?.status === "processing" && (
                  <p className="mt-3 inline-flex items-center gap-2 font-mono2 text-[10px] tracking-[0.25em] uppercase text-gold">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("video_processing")}
                  </p>
                )}
                {video?.status === "error" && (
                  <p className="mt-3 font-mono2 text-[10px] tracking-[0.25em] uppercase text-red-400">{t("video_error")}</p>
                )}
              </div>
            )}

            {(playing || started) && !done && (
              <AnimatePresence mode="wait">
                <motion.p key={`${idx}-${lang}`}
                  className="relative z-10 font-display font-bold text-xl sm:text-3xl lg:text-4xl leading-tight text-center max-w-3xl"
                  initial="hidden" animate="show" exit={{ opacity: 0, y: -24, transition: { duration: 0.3 } }}>
                  {scenes[idx].split(" ").map((w, i) => (
                    <span key={i} className="inline-block overflow-hidden align-bottom">
                      <motion.span className="inline-block"
                        variants={{ hidden: { y: "110%" }, show: { y: 0 } }}
                        transition={{ delay: i * 0.045, duration: 0.5, ease: [0.25, 1, 0.5, 1] }}>
                        {w}&nbsp;
                      </motion.span>
                    </span>
                  ))}
                </motion.p>
              </AnimatePresence>
            )}

            {done && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="relative z-10 text-center">
                <p className="font-serif-it text-2xl sm:text-3xl text-gold text-glow-gold mb-1">«{t("hero_sub")}»</p>
                <p className="font-mono2 text-xs tracking-[0.3em] uppercase text-mute">{t("story_continue")}</p>
              </motion.div>
            )}

            {!playing && (
              <button data-testid="teaser-play-button"
                onClick={() => { if (done) setElapsed(0); setPlaying(true); }}
                className="absolute inset-0 z-20 flex items-center justify-center group" aria-label="play">
                <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border border-ember/60 bg-black/60 backdrop-blur flex items-center justify-center transition-all duration-300 group-hover:scale-110 glow-orange">
                  {done ? <RotateCcw className="w-6 h-6 text-ember" /> : <Play className="w-6 h-6 text-ember fill-ember translate-x-0.5" />}
                </span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-4 px-4 sm:px-5 h-14 border-t border-white/10">
            <button data-testid="teaser-toggle-button"
              onClick={() => { if (done) setElapsed(0); setPlaying(!playing); }}
              className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-ember hover:text-ember transition-colors">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 translate-x-px" />}
            </button>
            <div data-testid="teaser-progress" onClick={seek}
              className="relative flex-1 h-1.5 rounded-full bg-white/10 cursor-pointer group">
              <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-100"
                style={{ width: `${(elapsed / total) * 100}%`, background: `linear-gradient(90deg, ${nar.color}, #FFB800)` }} />
              <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-gold opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `calc(${(elapsed / total) * 100}% - 6px)` }} />
            </div>
            <span className="font-mono2 text-[10px] sm:text-xs text-mist tabular-nums">{fmt(elapsed)} / {fmt(total)}</span>
          </div>
        </>
      )}

      {capped && (
        <div data-testid="video-gate-overlay" className="absolute inset-0 z-30 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center text-center px-6">
          <Lock className="w-8 h-8 text-gold mb-4" />
          <p className="font-display font-bold text-xl mb-2">{limited === "pay" ? t("gate_pay_title") : t("gate_video_register")}</p>
          <p className="text-mist text-sm mb-6 max-w-sm">{t("gate_video_sub")}</p>
          <Link
            to={limited === "pay" ? `/storia/${story.id}?v=read` : `/accedi?mode=register&return=/storia/${story.id}`}
            data-testid="video-gate-cta"
            className="rounded-full bg-ember px-7 py-3 text-xs font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors"
          >
            {limited === "pay" ? t("gate_pay_btn") : t("gate_reg_btn")}
          </Link>
        </div>
      )}
    </div>
  );
};

export default TeaserPlayer;
